import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ScheduleForm from './ScheduleForm';
import TermTimetableView from './TermTimetableView';
import { getSchedules, createSchedule, updateSchedule, deleteSchedule } from '../../api/scheduleApi';
import { getTeacherAssignments } from '../../api/teacherAssignmentApi';
import { getTermWeeks } from '../../api/termApi';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { useSchoolFeatures } from '../../common/utils/schoolSettings';
import { effectiveSlotTimes } from '../../common/utils/classTiming';

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const DAY_SHORT = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const SUBJECT_COLORS = [
  { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  { bg: '#fff3cd', border: '#f59e0b', text: '#92400e' },
  { bg: '#e0f2fe', border: '#0ea5e9', text: '#0c4a6e' },
  { bg: '#f3e8ff', border: '#a855f7', text: '#581c87' },
  { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  { bg: '#ecfdf5', border: '#22c55e', text: '#14532d' },
];

const ScheduleList = () => {
  const features = useSchoolFeatures();
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [selectedClassroom, setSelectedClassroom] = useState('all');
  const [selectedTeacher, setSelectedTeacher] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedTerm, setSelectedTerm] = useState('all');
  const [termWeeks, setTermWeeks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('week');
  const [displayView, setDisplayView] = useState('grid');

  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [activeSession, setActiveSession] = useState(null);

  const initialFormData = {
    session_id: null, grade_id: null, subject_id: null, teacher_id: null,
    classroom_id: null, day_of_week: null, start_time: null, end_time: null, notes: '',
    term_id: null, // null = runs for the whole session
  };
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { applyFilters(); }, [schedules, selectedClassroom, selectedTeacher, selectedGrade, selectedTerm, selectedDate, viewMode]);

  // Week availability for the picked term — drives the off-week warning.
  useEffect(() => {
    if (!selectedTerm || selectedTerm === 'all') { setTermWeeks([]); return; }
    getTermWeeks(selectedTerm)
      .then(res => setTermWeeks(res?.data || []))
      .catch(() => setTermWeeks([]));
  }, [selectedTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getSchedules({ withLookups: true });
      if (Array.isArray(data)) {
        setSchedules(data);
      } else {
        setSchedules(data?.items || []);
        const l = data?.lookups || {};
        const teachersArr = (l.teachers || []).map(t => ({ ...t, displayName: t?.user?.name || t?.name || 'Unnamed' }));
        setTeachers(teachersArr);
        setClassrooms(l.classrooms || []);
        const sessionsArr = l.sessions || [];
        setSessions(sessionsArr);
        setTerms(l.terms || []); // empty unless the school has terms enabled
        const active = sessionsArr.find(s => s?.is_active);
        setActiveSession(active || null);
        setGrades(l.grades || []);
        setSubjects(l.subjects || []);
        setAllGrades(l.grades || []);
        setAllSubjects(l.subjects || []);
      }
    } catch (error) {
      toast.error('Failed to fetch schedules.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...schedules];
    // A term view shows that term's slots plus the session-wide ones.
    if (selectedTerm && selectedTerm !== 'all') {
      filtered = filtered.filter(s => s.term_id === selectedTerm || s.term_id == null);
    }
    if (selectedClassroom && selectedClassroom !== 'all') filtered = filtered.filter(s => s.classroom_id === selectedClassroom);
    if (selectedTeacher && selectedTeacher !== 'all') filtered = filtered.filter(s => s.teacher_id === selectedTeacher);
    if (selectedGrade && selectedGrade !== 'all') filtered = filtered.filter(s => s.grade_id === selectedGrade);
    if (selectedDate) {
      const selectedDay = selectedDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      filtered = filtered.filter(s => s.day_of_week === selectedDay);
    } else if (viewMode === 'week') {
      filtered = filtered.filter(s => ['mon', 'tue', 'wed', 'thu', 'fri'].includes(s.day_of_week));
    } else if (viewMode === 'day') {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      filtered = filtered.filter(s => s.day_of_week === today);
    }
    setFilteredSchedules(filtered);
  };

  const classroomOptions = [{ label: 'All Classrooms', value: 'all' }, ...(classrooms.map(c => ({ label: c.name, value: c.id })))];
  const teacherOptions = [{ label: 'All Teachers', value: 'all' }, ...(teachers.map(t => ({ label: t.displayName || 'Unnamed', value: t.id })))];
  const gradeOptions = [{ label: 'All Grades', value: 'all' }, ...(allGrades.map(g => ({ label: g.name, value: g.id })))];
  const termOptions = [{ label: 'All Terms', value: 'all' }, ...terms.map(t => ({ label: t.name, value: t.id }))];

  // Which week of the selected term the picked date falls in (null when it
  // falls outside the term entirely).
  const weekForSelectedDate = (() => {
    if (!selectedDate || !termWeeks.length) return null;
    const d = selectedDate.toLocaleDateString('en-CA'); // YYYY-MM-DD, local
    return termWeeks.find(w =>
      String(w.week_start).slice(0, 10) <= d && d <= String(w.week_end).slice(0, 10)
    ) || null;
  })();

  const toHHMMSS = (dateVal) => {
    if (!dateVal) return null;
    const d = new Date(dateVal);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
  };

  const timeStringToDate = (timeStr) => {
    if (!timeStr) return null;
    const [hh = '00', mm = '00', ss = '00'] = String(timeStr).split(':');
    const d = new Date();
    d.setHours(parseInt(hh, 10)); d.setMinutes(parseInt(mm, 10)); d.setSeconds(parseInt(ss, 10)); d.setMilliseconds(0);
    return d;
  };

  const handleCreate = () => {
    setIsEditing(false); setCurrentRecord(null);
    setFormData({ ...initialFormData, session_id: activeSession?.id || null });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setIsEditing(true); setCurrentRecord(record);
    setFormData({
      session_id: record.session_id, grade_id: record.grade_id, subject_id: record.subject_id,
      teacher_id: record.teacher_id, classroom_id: record.classroom_id, day_of_week: record.day_of_week,
      start_time: timeStringToDate(record.start_time), end_time: timeStringToDate(record.end_time), notes: record.notes || '',
      term_id: record.term_id ?? null,
    });
    setIsModalVisible(true);
  };

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!formData.teacher_id) { setGrades(allGrades); setSubjects(allSubjects); return; }
      try {
        const assignments = await getTeacherAssignments(formData.teacher_id);
        const gIds = Array.from(new Set(assignments.map(a => a.grade?.id || a.grade_id).filter(Boolean)));
        const sIds = Array.from(new Set(assignments.map(a => a.subject?.id || a.subject_id).filter(Boolean)));
        setGrades(allGrades.filter(g => gIds.includes(g.id)));
        setSubjects(allSubjects.filter(s => sIds.includes(s.id)));
        if (formData.grade_id && !gIds.includes(formData.grade_id)) setFormData(p => ({ ...p, grade_id: null }));
        if (formData.subject_id && !sIds.includes(formData.subject_id)) setFormData(p => ({ ...p, subject_id: null }));
      } catch { setGrades(allGrades); setSubjects(allSubjects); }
    };
    fetchAssignments();
  }, [formData.teacher_id, allGrades, allSubjects]);

  const handleDelete = async (id) => {
    try { await deleteSchedule(id); toast.success('Schedule deleted'); fetchData(); }
    catch (error) { toast.error(error?.response?.data?.message || 'Failed to delete schedule'); }
  };

  const handleSubmit = async () => {
    const isSingleDay = !formData.pattern || formData.pattern === 'single';
    const missing = [];
    if (!formData.subject_id) missing.push('Subject');
    if (!formData.teacher_id) missing.push('Teacher');
    if (!formData.classroom_id) missing.push('Classroom');
    if (!formData.grade_id) missing.push('Grade');
    if (isSingleDay && !formData.day_of_week) missing.push('Day of Week');
    if (!formData.start_time) missing.push('Start Time');
    if (!formData.end_time) missing.push('End Time');
    if (missing.length) { toast.error(`Please fill: ${missing.join(', ')}`); return; }
    const payload = { ...formData, session_id: activeSession?.id || formData.session_id, start_time: toHHMMSS(formData.start_time), end_time: toHHMMSS(formData.end_time) };
    setLoading(true);
    try {
      if (isEditing && currentRecord?.id) { await updateSchedule(currentRecord.id, payload); toast.success('Schedule updated'); }
      else { await createSchedule(payload); toast.success('Schedule created'); }
      setIsModalVisible(false); fetchData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to save schedule';
      const err = error?.response?.data?.error;
      toast.error(err ? `${msg}: ${err}` : msg);
    } finally { setLoading(false); }
  };

  // ── Helpers ──────────────────────────────────────────────────────────
  const extractHour = (t) => {
    if (!t) return -1;
    const str = String(t);
    const timeStr = str.includes('T') ? (str.split('T')[1] || '') : str;
    return parseInt(timeStr.split(':')[0], 10);
  };

  const extractMinute = (t) => {
    if (!t) return 0;
    const str = String(t);
    const timeStr = str.includes('T') ? (str.split('T')[1] || '') : str;
    const parts = timeStr.split(':');
    return parseInt(parts[1] || '0', 10);
  };

  const fmtTime = (t) => {
    const h = extractHour(t);
    const m = extractMinute(t);
    if (h < 0) return '—';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return m > 0 ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12}:00 ${ampm}`;
  };

  // Effective class time for the school's timing mode (seasonal resolves to
  // today's season; per_schedule/per_term keep the slot's own time here).
  const slotRange = (s) => {
    const t = effectiveSlotTimes(s, features);
    return `${fmtTime(t.start_time)} – ${fmtTime(t.end_time)}`;
  };

  const getTeacherName = (id) => { const t = teachers.find(t => t.id === id); return t?.displayName || t?.name || '—'; };
  const getSubjectName = (id) => {
    const s = subjects.find(s => s.id === id) || allSubjects.find(s => s.id === id);
    return s?.name || null;
  };
  const getClassroomName = (id) => classrooms.find(c => c.id === id)?.name || '—';
  const getGradeName = (id) => {
    const g = allGrades.find(g => g.id === id);
    return g?.name || '—';
  };

  const subjectColor = (subjectId) => {
    if (!subjectId) return SUBJECT_COLORS[0];
    return SUBJECT_COLORS[subjectId % SUBJECT_COLORS.length];
  };

  // Dynamic time slots — derived from actual filtered schedule data
  const getDynamicTimeSlots = () => {
    const hourMinSet = new Map(); // hour -> Set of minutes
    filteredSchedules.forEach(s => {
      const h = extractHour(s.start_time);
      if (h >= 0) {
        if (!hourMinSet.has(h)) hourMinSet.set(h, new Set());
        hourMinSet.get(h).add(extractMinute(s.start_time));
      }
    });
    return Array.from(hourMinSet.keys()).sort((a, b) => a - b).map(h => {
      const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
      const ampm = h >= 12 ? 'PM' : 'AM';
      return { start: `${String(h).padStart(2, '0')}:00:00`, display: `${h12}:00 ${ampm}`, hour: h };
    });
  };

  const findSchedulesForSlot = (hour, day) =>
    filteredSchedules.filter(s => s.day_of_week === day && extractHour(s.start_time) === hour);

  const getDays = () => {
    if (selectedDate) return [selectedDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase()];
    if (viewMode === 'day') return [new Date().toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase()];
    if (viewMode === 'week') return ['mon', 'tue', 'wed', 'thu', 'fri'];
    return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  };

  const days = getDays();
  const timeSlots = getDynamicTimeSlots();

  const sortedSchedules = [...filteredSchedules].sort((a, b) => {
    const da = DAY_ORDER.indexOf(a.day_of_week);
    const db = DAY_ORDER.indexOf(b.day_of_week);
    if (da !== db) return da - db;
    const ha = extractHour(a.start_time) * 60 + extractMinute(a.start_time);
    const hb = extractHour(b.start_time) * 60 + extractMinute(b.start_time);
    return ha - hb;
  });

  const selectedGradeName = allGrades.find(g => g.id === selectedGrade)?.name;
  const hasGradeFilter = selectedGrade !== 'all';

  return (
    <div className="p-4">
      <Card>
        {/* Header */}
        <div className="flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold m-0">Class Schedule</h2>
            {hasGradeFilter && selectedGradeName && (
              <p className="text-color-secondary text-sm mt-1 m-0">{selectedGradeName}</p>
            )}
          </div>
          <div className="flex gap-2 align-items-center flex-wrap">
            <div className="flex surface-100 border-round p-1 gap-1">
              <Button icon="pi pi-table" label="Grid" size="small"
                text={displayView !== 'grid'} severity={displayView === 'grid' ? 'primary' : 'secondary'}
                onClick={() => setDisplayView('grid')} />
              <Button icon="pi pi-list" label="List" size="small"
                text={displayView !== 'list'} severity={displayView === 'list' ? 'primary' : 'secondary'}
                onClick={() => setDisplayView('list')} />
              {terms.length > 0 && (
                <Button icon="pi pi-calendar" label="Term" size="small"
                  text={displayView !== 'term'} severity={displayView === 'term' ? 'primary' : 'secondary'}
                  onClick={() => setDisplayView('term')} />
              )}
            </div>
            <Button label="+ Create Schedule" onClick={handleCreate} />
          </div>
        </div>

        {/* Filters + count — not applicable to the dated term view */}
        {displayView !== 'term' && (<>
        <div className="flex flex-wrap gap-3 mb-3 align-items-end">
          {/* Grade filter — most important */}
          <div style={{ minWidth: '150px', flex: '1 1 150px' }}>
            <label className="block text-xs font-semibold mb-1 text-color-secondary">Grade</label>
            <Dropdown value={selectedGrade} options={gradeOptions} onChange={e => setSelectedGrade(e.value)}
              placeholder="All Grades" className="w-full" filter showClear={selectedGrade !== 'all'} />
          </div>
          {/* Only rendered for schools that opted into terms */}
          {terms.length > 0 && (
            <div style={{ minWidth: '150px', flex: '1 1 150px' }}>
              <label className="block text-xs font-semibold mb-1 text-color-secondary">Term</label>
              <Dropdown value={selectedTerm} options={termOptions} onChange={e => setSelectedTerm(e.value)}
                placeholder="All Terms" className="w-full" showClear={selectedTerm !== 'all'} />
            </div>
          )}
          <div style={{ minWidth: '150px', flex: '1 1 150px' }}>
            <label className="block text-xs font-semibold mb-1 text-color-secondary">Teacher</label>
            <Dropdown value={selectedTeacher} options={teacherOptions} onChange={e => setSelectedTeacher(e.value)}
              placeholder="All Teachers" className="w-full" filter showClear={selectedTeacher !== 'all'} />
          </div>
          <div style={{ minWidth: '150px', flex: '1 1 150px' }}>
            <label className="block text-xs font-semibold mb-1 text-color-secondary">Classroom</label>
            <Dropdown value={selectedClassroom} options={classroomOptions} onChange={e => setSelectedClassroom(e.value)}
              placeholder="All Classrooms" className="w-full" filter showClear={selectedClassroom !== 'all'} />
          </div>
          <div style={{ minWidth: '140px', flex: '0 1 140px' }}>
            <label className="block text-xs font-semibold mb-1 text-color-secondary">Date</label>
            <Calendar value={selectedDate} onChange={e => setSelectedDate(e.value)} dateFormat="dd/mm/yy"
              showIcon placeholder="Filter by date" className="w-full" />
          </div>
          {displayView === 'grid' && !selectedDate && (
            <div className="flex surface-100 border-round p-1 gap-1 align-self-end">
              {['week', 'month', 'day'].map(m => (
                <Button key={m} label={m.charAt(0).toUpperCase() + m.slice(1)} size="small"
                  text={viewMode !== m} severity={viewMode === m ? 'primary' : 'secondary'}
                  onClick={() => setViewMode(m)} />
              ))}
            </div>
          )}
          {selectedDate && (
            <Button icon="pi pi-times" label="Clear" size="small" text severity="secondary"
              className="align-self-end" onClick={() => setSelectedDate(null)} />
          )}
        </div>

        {/* Off-week / out-of-term warning for the picked date */}
        {selectedDate && termWeeks.length > 0 && (!weekForSelectedDate || !weekForSelectedDate.is_available) && (
          <div className="p-2 mb-3 border-round text-sm"
            style={{ background: 'var(--yellow-100)', color: 'var(--yellow-900)' }}>
            <i className="pi pi-exclamation-triangle mr-2" />
            {weekForSelectedDate
              ? `Week ${weekForSelectedDate.week_no} is marked unavailable — no classes run on this date.`
              : 'This date falls outside the selected term.'}
          </div>
        )}

        {/* Count bar */}
        <div className="text-sm text-color-secondary mb-3 flex align-items-center gap-2">
          {loading ? (
            <span><i className="pi pi-spin pi-spinner mr-1" />Loading…</span>
          ) : filteredSchedules.length === 0 ? (
            <span style={{ color: 'var(--orange-500)' }}>
              No schedules found.{schedules.length > 0 ? ` (${schedules.length} total — try adjusting filters)` : ''}
            </span>
          ) : (
            <>
              <strong>{filteredSchedules.length}</strong> schedule{filteredSchedules.length !== 1 ? 's' : ''}
              {!hasGradeFilter && filteredSchedules.length > 30 && (
                <Tag severity="warning" value="Filter by Grade for a cleaner view" icon="pi pi-info-circle" />
              )}
            </>
          )}
        </div>
        </>)}

        {/* ── TERM VIEW ── */}
        {displayView === 'term' && terms.length > 0 && (
          <TermTimetableView terms={terms} />
        )}

        {/* ── LIST VIEW ── */}
        {displayView === 'list' && (
          <DataTable value={sortedSchedules} size="small" stripedRows paginator rows={20}
            emptyMessage="No schedules found." className="border-1 surface-border border-round">
            <Column header="Day" body={r => <span className="font-semibold">{DAY_SHORT[r.day_of_week] || r.day_of_week}</span>} style={{ width: '4.5rem' }} />
            <Column header="Time" body={r => (
              <span style={{ whiteSpace: 'nowrap' }}>{slotRange(r)}</span>
            )} style={{ width: '9rem' }} />
            <Column header="Grade" body={r => getGradeName(r.grade_id)} style={{ width: '7rem' }} />
            <Column header="Subject" body={r => {
              const name = getSubjectName(r.subject_id);
              const col = subjectColor(r.subject_id);
              return name ? (
                <span className="px-2 py-1 border-round text-xs font-semibold" style={{ background: col.bg, color: col.text }}>
                  {name}
                </span>
              ) : '—';
            }} />
            <Column header="Teacher" body={r => getTeacherName(r.teacher_id)} />
            <Column header="Classroom" body={r => getClassroomName(r.classroom_id)} style={{ width: '7rem' }} />
            <Column header="" style={{ width: '5rem' }} body={r => (
              <div className="flex gap-1">
                <Button icon="pi pi-pencil" text size="small" onClick={() => handleEdit(r)} tooltip="Edit" tooltipOptions={{ position: 'top' }} />
                <Button icon="pi pi-trash" text severity="danger" size="small" onClick={() => handleDelete(r.id)} tooltip="Delete" tooltipOptions={{ position: 'top' }} />
              </div>
            )} />
          </DataTable>
        )}

        {/* ── GRID VIEW ── */}
        {displayView === 'grid' && (
          <>
            {filteredSchedules.length === 0 && !loading ? null : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '5.5rem' }} />
                    {days.map(d => <col key={d} />)}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="p-2 text-xs font-semibold text-color-secondary border-bottom-1 surface-border text-left"
                        style={{ background: 'var(--surface-50)', borderRight: '1px solid var(--surface-border)' }}>
                        Time
                      </th>
                      {days.map(day => (
                        <th key={day} className="p-2 text-center text-sm font-semibold border-bottom-1 surface-border"
                          style={{ background: 'var(--surface-50)' }}>
                          {DAY_LABELS[day]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.length === 0 ? (
                      <tr>
                        <td colSpan={days.length + 1} className="p-4 text-center text-color-secondary">
                          No schedules to display.
                        </td>
                      </tr>
                    ) : timeSlots.map((slot) => {
                      // Only render row if at least one day has a schedule
                      const rowItems = days.map(d => findSchedulesForSlot(slot.hour, d));
                      const hasAny = rowItems.some(items => items.length > 0);
                      if (!hasAny) return null;
                      return (
                        <tr key={slot.hour}>
                          <td className="p-2 text-xs font-semibold text-color-secondary border-bottom-1 surface-border"
                            style={{ whiteSpace: 'nowrap', background: 'var(--surface-50)', borderRight: '1px solid var(--surface-border)', verticalAlign: 'top', paddingTop: '0.6rem' }}>
                            {slot.display}
                          </td>
                          {days.map((day, di) => {
                            const items = rowItems[di];
                            return (
                              <td key={day} className="border-bottom-1 border-right-1 surface-border p-1 align-top"
                                style={{ minHeight: '4rem', verticalAlign: 'top' }}>
                                {items.length > 0 ? (
                                  <div className="flex flex-column gap-1">
                                    {items.map(s => {
                                      const col = subjectColor(s.subject_id);
                                      const subjectName = getSubjectName(s.subject_id);
                                      return (
                                        <div key={s.id} className="border-round p-2 text-xs"
                                          style={{ background: col.bg, borderLeft: `3px solid ${col.border}` }}>
                                          <div className="font-bold mb-1" style={{ color: col.text }}>
                                            {subjectName || '—'}
                                          </div>
                                          <div className="text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>
                                            {slotRange(s)}
                                          </div>
                                          <div className="text-color-secondary" style={{ fontSize: '0.7rem' }}>
                                            <i className="pi pi-user mr-1" style={{ fontSize: '0.6rem' }} />
                                            {getTeacherName(s.teacher_id)}
                                          </div>
                                          {!hasGradeFilter && (
                                            <div className="text-color-secondary" style={{ fontSize: '0.7rem' }}>
                                              <i className="pi pi-book mr-1" style={{ fontSize: '0.6rem' }} />
                                              {getGradeName(s.grade_id)}
                                            </div>
                                          )}
                                          <div className="flex gap-1 mt-1">
                                            <Button icon="pi pi-pencil" text size="small"
                                              style={{ padding: '0.1rem 0.25rem', height: 'auto', width: 'auto' }}
                                              onClick={() => handleEdit(s)} />
                                            <Button icon="pi pi-trash" text severity="danger" size="small"
                                              style={{ padding: '0.1rem 0.25rem', height: 'auto', width: 'auto' }}
                                              onClick={() => handleDelete(s.id)} />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div style={{ height: '100%', minHeight: '2.5rem' }} />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      <ScheduleForm
        isModalVisible={isModalVisible} setIsModalVisible={setIsModalVisible}
        isEditing={isEditing} formData={formData} setFormData={setFormData}
        handleSubmit={handleSubmit} loading={loading} sessions={sessions}
        activeSession={activeSession} grades={grades} subjects={subjects}
        teachers={teachers} classrooms={classrooms} terms={terms}
      />
    </div>
  );
};

export default ScheduleList;
